import { useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AwsAccount, RoleValidationResult } from '../../../types/backend';
import { AWSAccountConfig } from '../../../types';
import { useAwsAccountService } from '../services/awsAccountService';
import { useEntityManagement, EntityCrudService } from '../../../shared/hooks/useEntityManagement';
import { awsAccountKeys } from './queryKeys';

export interface AccountFormData {
  aws_account_id: string;
  account_name: string;
  read_role_name: string;
  write_role_name: string;
  automation_role_name: string;
  /** The seed role the customer installs. Ours only in the sense that we assume it. */
  onboarding_role_name: string;
  external_id: string;
  main_region: string;
  supported_regions: string[];
  event_bus_name: string;
  /** A disabled account is skipped entirely, so nothing onboards it. */
  enabled: boolean;
  /** Registration only: whether Cloud2 stays read-only here until the customer
      breaks glass in their own tenant. On a saved account it is the Baselines
      tab's switch, read from and written to account_baseline directly. */
  break_glass_required: boolean;
}

/** The subset of the account service these writes need. */
export interface AccountWriteService {
  getCustomerAwsAccounts: (customerId: string) => Promise<{ customer_accounts: AwsAccount[] }>;
  createCustomerAwsAccount: (customerId: string, dto: AWSAccountConfig) => Promise<AwsAccount>;
  updateCustomerAwsAccount: (customerId: string, id: string, dto: Partial<AWSAccountConfig>) => Promise<AwsAccount>;
  setAccountBreakGlass: (customerId: string, awsAccountId: string, required: boolean) => Promise<unknown>;
}

const errorMessage = (cause: unknown) =>
  cause instanceof Error ? cause.message : String(cause);

/**
 * Reading and writing an account record.
 *
 * Break-glass is the one decision made at registration, because it decides how
 * the access roles are built and they are installed once. It travels on its own
 * admin action after the account exists, and only a yes is sent: the form
 * always carries it, so sending it whenever present would make every
 * registration an admin action. While account_baseline is off every baseline
 * action is refused, so it is not sent at all.
 */
export function accountWrites(service: AccountWriteService, customerId: string, baselineEnabled: boolean) {
  return {
    getAll: async (): Promise<AwsAccount[]> =>
      (await service.getCustomerAwsAccounts(customerId)).customer_accounts,
    create: async (dto: AWSAccountConfig): Promise<AwsAccount> => {
      const { break_glass_required, ...rest } = dto;
      const created = await service.createCustomerAwsAccount(customerId, rest);
      if (!baselineEnabled || break_glass_required !== true) return created;
      // The account exists by this point, so a failure here cannot be reported
      // as "registration failed": the admin would believe nothing happened
      // while Cloud2 keeps standing admin in an account meant to clamp it.
      try {
        await service.setAccountBreakGlass(customerId, created.aws_account_id, true);
      } catch (cause) {
        throw Object.assign(
          new Error(
            `The account was registered, but "Customer controls our admin access" was not switched on: ` +
            `${errorMessage(cause)}. Open the account and switch it on.`
          ),
          { cause }
        );
      }
      return created;
    },
    update: (id: string, dto: Partial<AWSAccountConfig>): Promise<AwsAccount> =>
      service.updateCustomerAwsAccount(customerId, id, dto),
  };
}

/** The two role names that point at an administrator role. '' is no role. */
export interface AdminRoles {
  automation_role_name: string;
  onboarding_role_name: string;
}

const ADMIN_ROLE_FIELDS: Array<keyof AdminRoles> = ['automation_role_name', 'onboarding_role_name'];

/**
 * The seed role the customer installs by hand (account-onboarding-role.yml).
 * The server stores it on every new account, so the form starts from it.
 */
export const ONBOARDING_ROLE_NAME = 'cloud2-onboarding';

/**
 * What the form saves: the account fields plus the two admin role names.
 * Only withAdminRoles takes it; it hands create and update an AWSAccountConfig,
 * whose type refuses both names.
 */
export type AccountFormDto = Omit<AWSAccountConfig, keyof AdminRoles> & Partial<AdminRoles>;

/** Record an account's two admin role names as last loaded from the server. */
export function rememberAdminRoles(
  saved: Record<string, AdminRoles>,
  account: { id: string; automation_role_name?: string | null; onboarding_role_name?: string | null }
): void {
  saved[account.id] = {
    automation_role_name: account.automation_role_name || '',
    onboarding_role_name: account.onboarding_role_name || '',
  };
}

/** The account DTO without the two admin role names, which never ride along in it. */
function splitAdminRoles<T extends Partial<AccountFormDto>>(dto: T): [Omit<T, keyof AdminRoles>, Partial<AdminRoles>] {
  const { automation_role_name, onboarding_role_name, ...rest } = dto;
  return [rest, { automation_role_name, onboarding_role_name }];
}

/** The named roles that differ from `saved`. */
function adminRolesToSend(
  requested: Partial<AdminRoles>,
  saved: AdminRoles | undefined
): Partial<AdminRoles> | null {
  const changes: Partial<AdminRoles> = {};
  for (const field of ADMIN_ROLE_FIELDS) {
    const value = requested[field];
    if (value && value !== (saved?.[field] ?? '')) changes[field] = value;
  }
  return Object.keys(changes).length ? changes : null;
}

/** The subset of the account service the admin role names need. */
export interface AdminRolesService {
  setAwsAccountAdminRoles: (customerId: string, id: string, roles: Partial<AdminRoles>) => Promise<unknown>;
}

interface AccountWrites<Dto> {
  getAll: () => Promise<AwsAccount[]>;
  create: (dto: Dto) => Promise<AwsAccount>;
  update: (id: string, dto: Partial<Dto>) => Promise<AwsAccount>;
}

/**
 * The account writes, with the automation and onboarding role names split off
 * onto customers.SetAWSAccountAdminRoles.
 *
 * Both name an administrator role Cloud2 assumes in the customer's account, so
 * the server takes them only on that admin action and refuses them inside
 * create and update. Below admin they are never sent; for an admin they are
 * sent only when they moved, before the rest of an edit.
 */
export function withAdminRoles(
  writes: AccountWrites<AWSAccountConfig>,
  service: AdminRolesService,
  customerId: string,
  allowAdminRoles: boolean,
  saved: Record<string, AdminRoles>
): AccountWrites<AccountFormDto> {
  return {
    getAll: async () => {
      const accounts = await writes.getAll();
      Object.keys(saved).forEach(k => delete saved[k]);
      accounts.forEach(a => rememberAdminRoles(saved, a));
      return accounts;
    },
    create: async (dto) => {
      const [rest, roles] = splitAdminRoles(dto);
      const created = await writes.create(rest);
      // The server stores the fixed seed role on every registration, so the
      // admin call carries only what differs from the record it just wrote.
      rememberAdminRoles(saved, created);
      const changes = allowAdminRoles ? adminRolesToSend(roles, saved[created.id]) : null;
      if (changes === null) return created;
      try {
        await service.setAwsAccountAdminRoles(customerId, created.id, changes);
      } catch (cause) {
        throw Object.assign(
          new Error(
            `The account was registered, but its automation and onboarding roles were not set: ` +
            `${errorMessage(cause)}. Open the account and set them.`
          ),
          { cause }
        );
      }
      const withRoles = { ...created, ...changes };
      rememberAdminRoles(saved, withRoles);
      return withRoles;
    },
    update: async (id, dto) => {
      const [rest, roles] = splitAdminRoles(dto);
      const changes = allowAdminRoles ? adminRolesToSend(roles, saved[id]) : null;
      if (changes !== null) {
        await service.setAwsAccountAdminRoles(customerId, id, changes);
      }
      const updated = await writes.update(id, rest);
      rememberAdminRoles(saved, updated);
      return updated;
    },
  };
}

/** A field of the account form. */
export type AccountField = keyof AccountFormData;

/**
 * The fields each tab of a saved account edits and saves, and no other tab
 * does. Break-glass is on none of these: on a saved account the Baselines tab
 * sets it on its own action.
 */
export const IDENTITY_FIELDS: readonly AccountField[] = ['account_name', 'enabled'];
export const ACCESS_FIELDS: readonly AccountField[] = [
  'read_role_name', 'write_role_name', 'automation_role_name', 'onboarding_role_name', 'external_id', 'event_bus_name',
];
export const REGION_FIELDS: readonly AccountField[] = ['supported_regions', 'main_region'];

/** Every field a saved account's tabs edit. */
const EDITABLE_FIELDS: readonly AccountField[] = [...IDENTITY_FIELDS, ...ACCESS_FIELDS, ...REGION_FIELDS];

/** The form a saved account loads into, with no side effects. */
export function accountToForm(account: AwsAccount): AccountFormData {
  return {
    aws_account_id: account.aws_account_id,
    account_name: account.account_name,
    read_role_name: account.read_role_name,
    write_role_name: account.write_role_name || '',
    automation_role_name: account.automation_role_name || '',
    onboarding_role_name: account.onboarding_role_name || '',
    external_id: account.external_id || '',
    main_region: account.main_region,
    supported_regions: account.supported_regions || [],
    event_bus_name: account.event_bus_name || 'cloud2-events',
    enabled: account.enabled,
    break_glass_required: false,
  };
}

const comparable = (value: unknown) => JSON.stringify(Array.isArray(value) ? [...value].sort() : value);

/** Whether any of `fields` on the form differs from the saved account. */
export function sectionHasChanges(
  form: AccountFormData,
  account: AwsAccount,
  fields: readonly AccountField[]
): boolean {
  const saved = accountToForm(account);
  return fields.some(field => comparable(form[field]) !== comparable(saved[field]));
}

/**
 * Whether the form differs from the saved account, over every field the tabs
 * edit. A new account is always a change.
 */
export function accountHasChanges(
  form: AccountFormData,
  account: AwsAccount | null,
  isNew: boolean
): boolean {
  if (isNew) return true;
  if (!account) return false;
  return sectionHasChanges(form, account, EDITABLE_FIELDS);
}

const EMPTY_FORM: AccountFormData = {
  aws_account_id: '',
  account_name: '',
  read_role_name: 'cloud2-operator-readonly',
  write_role_name: 'cloud2-operator-admin',
  automation_role_name: '',
  onboarding_role_name: ONBOARDING_ROLE_NAME,
  external_id: '',
  main_region: 'eu-central-1',
  supported_regions: ['eu-central-1'],
  event_bus_name: 'cloud2-events',
  enabled: true,
  break_glass_required: false
};

export const getAccountIdError = (accountId: string, accounts: AwsAccount[], selectedId?: string): string | null => {
  if (!accountId) return null;
  if (!/^\d+$/.test(accountId)) return "Must contain only numbers";
  if (accountId.length < 12) return "Must be exactly 12 digits";
  const isDuplicate = accounts.some(acc =>
    acc.aws_account_id === accountId && acc.id !== selectedId
  );
  if (isDuplicate) return "This account ID is already configured";
  return null;
};

export const getRoleNameError = (roleName: string): string | null => {
  if (!roleName) return null;
  if (!/^[\w+=,.@-]+$/.test(roleName)) {
    return "Invalid role name format (allowed: alphanumeric, +=,.@-)";
  }
  if (roleName.length > 64) return "Must be 64 characters or less";
  return null;
};

/** Each field's error, null where it is valid. */
export const validateAccountForm = (form: AccountFormData): Partial<Record<AccountField, string | null>> => ({
  aws_account_id: !form.aws_account_id || form.aws_account_id.length !== 12 || !/^\d{12}$/.test(form.aws_account_id)
    ? 'Must be a 12-digit AWS account ID' : null,
  account_name: !form.account_name?.trim() ? 'Account name is required' : null,
  read_role_name: !form.read_role_name?.trim() ? 'Read role is required' : getRoleNameError(form.read_role_name),
  write_role_name: form.write_role_name ? getRoleNameError(form.write_role_name) : null,
  automation_role_name: form.automation_role_name ? getRoleNameError(form.automation_role_name) : null,
  onboarding_role_name: form.onboarding_role_name ? getRoleNameError(form.onboarding_role_name) : null,
  supported_regions: !form.supported_regions?.length ? 'At least one region required' : null,
  main_region: !form.supported_regions?.includes(form.main_region) ? 'Main region must be in supported regions' : null,
});

/**
 * Optional fields an edit cannot empty. The account update leaves an empty
 * value out, and the admin role call sends only a name, so a cleared field
 * would keep its stored value while the tab said it saved.
 */
const UNCLEARABLE_FIELDS: readonly AccountField[] = [
  'write_role_name', 'automation_role_name', 'onboarding_role_name', 'external_id', 'event_bus_name',
];

/** Each field's error on a saved account: the form's own rules, and no emptying a field that is set. */
export function accountFieldErrors(form: AccountFormData, account: AwsAccount): Partial<Record<AccountField, string | null>> {
  const errors = validateAccountForm(form);
  const saved = accountToForm(account);
  for (const field of UNCLEARABLE_FIELDS) {
    if (!errors[field] && saved[field] && !String(form[field]).trim()) {
      errors[field] = 'Cannot be emptied once set: enter the value to use instead';
    }
  }
  return errors;
}

interface UseAccountFormOptions {
  customerId: string;
  isOpen: boolean;
  /** Whether account_baseline is on. Off, a registration never sends
      break-glass, since every baseline action is refused. */
  baselineEnabled: boolean;
  /** Whether the caller is admin on this customer. Below admin the automation
      and onboarding role names are never sent: only an admin may set them. */
  allowAdminRoles: boolean;
}

export function useAccountForm({ customerId, isOpen, baselineEnabled, allowAdminRoles }: UseAccountFormOptions) {
  const awsAccountService = useAwsAccountService();

  // `enabled` is on the update request only; create has no such field and the
  // backend forbids extras, so sending it on create would fail the call.
  const formToCreateDto = (form: AccountFormData): AccountFormDto => ({
    customer_id: customerId,
    aws_account_id: form.aws_account_id,
    account_name: form.account_name,
    read_role_name: form.read_role_name,
    write_role_name: form.write_role_name || undefined,
    automation_role_name: form.automation_role_name || undefined,
    onboarding_role_name: form.onboarding_role_name || undefined,
    external_id: form.external_id || undefined,
    main_region: form.main_region,
    supported_regions: form.supported_regions,
    event_bus_name: form.event_bus_name || undefined,
    break_glass_required: form.break_glass_required,
  });

  /** The update fields: everything a tab edits, never break-glass. */
  const formToUpdateDto = (form: AccountFormData): Partial<AccountFormDto> => {
    const dto: Partial<AccountFormDto> = { ...formToCreateDto(form), enabled: form.enabled };
    delete dto.break_glass_required;
    return dto;
  };

  // The two admin role names as last loaded, so a save sends them only when they moved.
  const savedAdminRoles = useRef<Record<string, AdminRoles>>({});

  const crudService = useMemo((): EntityCrudService<AwsAccount, AccountFormDto, Partial<AccountFormDto>> => ({
    ...withAdminRoles(
      accountWrites(awsAccountService, customerId, baselineEnabled),
      awsAccountService, customerId, allowAdminRoles, savedAdminRoles.current
    ),
    delete: (id) => awsAccountService.deleteCustomerAwsAccount(customerId, id),
    testAccess: (id, roleType: 'readonly' | 'write' | 'automation', region: string) =>
      awsAccountService.testCustomerRoleAccess(customerId, id, roleType, region)
  }), [awsAccountService, customerId, baselineEnabled, allowAdminRoles]);

  const queryKey = awsAccountKeys.accounts(customerId);
  const queryClient = useQueryClient();

  const entityManagement = useEntityManagement<AwsAccount, AccountFormData>({
    service: crudService,
    queryKey,
    isOpen,
    emptyForm: EMPTY_FORM,
    getEntityId: (account) => account.id,
    entityToForm: (account) => (
      rememberAdminRoles(savedAdminRoles.current, account),
      accountToForm(account)
    ),
    formToCreateDto,
    formToUpdateDto,
    validateForm: validateAccountForm,
    hasFormChanges: accountHasChanges
  });

  /**
   * Save one tab's fields of the selected account and nothing else. Fields
   * other tabs hold unsaved stay as typed. Throws with the reason on an
   * invalid field or a refused write, for the tab to show beside its Save.
   */
  const saveFields = async (fields: readonly AccountField[]) => {
    const selected = entityManagement.selectedEntity;
    if (!selected) throw new Error('No account is selected');
    const form = entityManagement.formData;
    const errors = accountFieldErrors(form, selected);
    const invalid = fields.map(field => errors[field]).find(Boolean);
    if (invalid) throw new Error(invalid);
    const full: Record<string, unknown> = formToUpdateDto(form);
    const dto = Object.fromEntries(fields.map(field => [field, full[field]])) as Partial<AccountFormDto>;
    const updated = await crudService.update(selected.id, dto);
    await queryClient.invalidateQueries({ queryKey });
    entityManagement.refreshSelected(updated, (typed, fresh) => ({
      ...typed,
      ...Object.fromEntries(fields.map(field => [field, fresh[field]])),
    }));
  };

  return {
    entities: entityManagement.entities,
    loading: entityManagement.loading,
    error: entityManagement.error,
    selectedEntity: entityManagement.selectedEntity,
    formData: entityManagement.formData,
    saving: entityManagement.saving,
    isNew: entityManagement.isNew,
    showDeleteConfirm: entityManagement.showDeleteConfirm,
    successMessage: entityManagement.successMessage,
    testing: entityManagement.testing,
    testResult: entityManagement.testResult as RoleValidationResult | null,
    handleSelect: entityManagement.handleSelect,
    handleAddNew: entityManagement.handleAddNew,
    handleSave: entityManagement.handleSave,
    handleDeleteClick: entityManagement.handleDeleteClick,
    handleDeleteConfirm: entityManagement.handleDeleteConfirm,
    handleDeleteCancel: entityManagement.handleDeleteCancel,
    handleChange: entityManagement.handleChange,
    handleTestRole: entityManagement.handleTestRole,
    setError: entityManagement.setError,
    setSuccessMessage: entityManagement.setSuccessMessage,
    isFormValid: entityManagement.isFormValid,
    hasChanges: entityManagement.hasChanges,
    saveFields,
    customerId,
  };
}
